<script setup lang="ts">
import type { Table } from '@tanstack/vue-table'
import { Member } from '@/types/Member'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
	DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SlidersHorizontal } from 'lucide-vue-next'
import { computed } from 'vue'

interface UserDataTableViewOptionsProps {
	table: Table<Member>
}

const props = defineProps<UserDataTableViewOptionsProps>()

// Friendly display names for column IDs
const columnLabels: Record<string, string> = {
	email: 'Email',
	firstname: 'First Name',
	lastname: 'Last Name',
	user: 'Username',
	active: 'Active',
	disabled: 'Disabled',
	city: 'City',
	country: 'Country',
	created: 'Created',
	lastlogin: 'Last Login',
	publicfiles: 'Public Files',
	approvedfiles: 'Approved',
	unmoderatedfiles: 'Unmoderated',
	deniedfiles: 'Denied',
	followers: 'Followers',
	following: 'Following',
	phone: 'Phone',
}

const columns = computed(() => props.table.getAllColumns()
	.filter(
		column =>
			typeof column.accessorFn !== 'undefined' && column.getCanHide(),
	))

const getLabel = (id: string) => columnLabels[id] || id
</script>

<template>
	<DropdownMenu>
		<DropdownMenuTrigger as-child>
			<Button
				variant="outline"
				size="sm"
				class="ml-auto h-8 gap-1.5 text-xs"
			>
				<SlidersHorizontal class="h-3.5 w-3.5" />
				Columns
			</Button>
		</DropdownMenuTrigger>
		<DropdownMenuContent align="end" class="w-[180px]">
			<DropdownMenuLabel class="text-xs">Toggle columns</DropdownMenuLabel>
			<DropdownMenuSeparator />

			<DropdownMenuCheckboxItem
				v-for="column in columns"
				:key="column.id"
				class="text-xs"
				:checked="column.getIsVisible()"
				@update:checked="(value: boolean) => column.toggleVisibility(!!value)"
			>
				{{ getLabel(column.id) }}
			</DropdownMenuCheckboxItem>
		</DropdownMenuContent>
	</DropdownMenu>
</template>
